// src/data/disciplineQueries.js - Record 577, ruling 56 (broken-now item 1 of Record 575).
// A discipline DRAFT belongs to its author (and the administrator) until it is opened as a case. firestore.rules now
// refuses a draft to every other arm, so a listing query must be SHAPED so the rules can prove it never returns one:
// officers ask for status IN the non-draft stages; their own drafts come from a separate createdBy query.
// One place for every discipline listing (DisciplineCases, MemberFiles, Reports, UnionCalendar) - directives XI.
import { collection, query, where } from 'firebase/firestore';
import { holdsTestTerritory, isTesterAccount, keepForViewer, TEST_SCOPE } from './testTerritory';   // Record 613 (Bob 10/8), F22
import { capScopes } from './scopeCap';   // T5 item 20: the 30-territory cap, in one place

// The case ladder (DisciplineCases.js CASE_STAGES) plus gc_review. 'draft' is deliberately absent.
export const NON_DRAFT_CASE_STATUSES = ['notice_received', 'investigation_ordered', 'investigation_complete',
  'appeal_filed', 'decision_pending', 'resolved', 'gc_review'];

function roleOf(user) {
  if (!user) return null;
  if (user.isAdmin || user.role === 'admin') return 'admin';
  if (user.role === 'general_chair' && user.committee) return 'gc';
  if (user.role === 'local_chair' && Array.isArray(user.scopes) && user.scopes.filter(Boolean).length) return 'lc';
  if (isTesterAccount(user)) return 'tester';   // Record 613 (Bob 10/8), F22
  return 'own';
}

// Returns the list of queries this user may run; results are merged by id by the caller.
export function visibleCaseQueries(db, user) {
  const col = collection(db, 'disciplineCases');
  const r = roleOf(user);
  if (!r) return [];
  const own = user && user.uid ? [query(col, where('createdBy', '==', user.uid))] : [];
  // Ruling 66 (Record 582): the administrator lists non-drafts and his own; other drafts only via adminReadDraft.
  if (r === 'admin') return [query(col, where('status', 'in', NON_DRAFT_CASE_STATUSES)), ...own];
  if (r === 'gc') return [query(col, where('generalCommittee', '==', user.committee), where('status', 'in', NON_DRAFT_CASE_STATUSES)), ...own];
  if (r === 'lc') return [...capScopes(user).scopes.map(s =>
    query(col, where('scopeKey', '==', s), where('status', 'in', NON_DRAFT_CASE_STATUSES))), ...own];
  // Record 613 (Bob 10/8), F22: a tester lists his own cases (his example drafts among them) and the OPENED EXAMPLES of
  // the test territory (the rules' testerSandbox() arm: the territory, the mark and the non-draft stages, all named in the
  // query). Never the member's "about me" queries below - namedMember() is a member's arm, the rules refuse those to a
  // tester, and that refusal failed his whole case list.
  if (r === 'tester') return [...own, ...(holdsTestTerritory(user)
    ? [query(col, where('scopeKey', '==', TEST_SCOPE), where('isExample', '==', true), where('status', 'in', NON_DRAFT_CASE_STATUSES))] : [])];
  // Record 586 (functions audit F9, HIGH): a member also sees the filed cases that NAME him (ruling 176 read arm),
  // not only the ones he opened. Drafts stay their author's (ruling 56).
  const about = [];
  if (user && user.uid) about.push(query(col, where('memberUid', '==', user.uid), where('status', 'in', NON_DRAFT_CASE_STATUSES)));
  if (user && user.email) about.push(query(col, where('memberEmail', '==', user.email), where('status', 'in', NON_DRAFT_CASE_STATUSES)));
  return [...own, ...about];
}

// Subscribe to every query above, merged by id. onRows(rows) on each change; onError(err) if any query is refused.
// Returns the unsubscribe function.
export function subscribeVisibleCases(db, user, onSnapshot, onRows, onError) {
  const qs = visibleCaseQueries(db, user);
  if (!qs.length) { onRows([]); return () => {}; }
  const sets = qs.map(() => []);
  const unsubs = qs.map((q, i) => onSnapshot(q, snap => {
    sets[i] = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const byId = new Map(); for (const s of sets) for (const r of s) byId.set(r.id, r);
    onRows(keepForViewer(user, [...byId.values()]));   // Record 613 (Bob 10/8), F22: no example in a real officer's list
  }, e => { if (onError) onError(e); }));
  return () => unsubs.forEach(u => u());
}
